import { useControl } from 'react-map-gl';

const LANGS = ['local', 'en', 'ar'];
const LABELS = { local: 'עב', en: 'EN', ar: 'ع' };
const TITLES = { local: 'Local labels', en: 'English labels', ar: 'Arabic labels' };

class LanguageControl {
  constructor() {
    this._lang = 'local';
    this._originalFields = null; // deep-cloned snapshot of original text-field per layer
  }

  _snapshotOriginals(map) {
    const fields = {};
    map.getStyle().layers.forEach(layer => {
      if (layer.type === 'symbol' && layer.layout?.['text-field'] && layer.id !== 'pointLayer') {
        // Deep clone to protect from mutations
        fields[layer.id] = JSON.parse(JSON.stringify(layer.layout['text-field']));
      }
    });
    this._originalFields = fields;
  }

  _applyLanguage(map) {
    if (!map.isStyleLoaded()) return;
    if (!this._originalFields) {
      this._snapshotOriginals(map);
    }
    const lang = this._lang;
    map.getStyle().layers.forEach(layer => {
      if (layer.type === 'symbol' && layer.layout?.['text-field'] && layer.id !== 'pointLayer') {
        if (lang === 'local') {
          const original = this._originalFields[layer.id];
          if (original) {
            map.setLayoutProperty(layer.id, 'text-field', JSON.parse(JSON.stringify(original)));
          }
        } else {
          const field = lang === 'en'
            ? ['get', 'name_en']
            : ['coalesce', ['get', 'name_ar'], ['get', 'name']];
          map.setLayoutProperty(layer.id, 'text-field', field);
        }
      }
    });
  }

  onAdd(map) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

    this._button = document.createElement('button');
    this._button.type = 'button';
    this._button.title = TITLES[this._lang];
    this._button.textContent = LABELS[this._lang];
    this._button.style.cssText = 'font-weight:bold;font-size:13px;width:29px;height:29px;cursor:pointer;';

    this._button.addEventListener('click', () => {
      const idx = (LANGS.indexOf(this._lang) + 1) % LANGS.length;
      this._lang = LANGS[idx];
      this._button.textContent = LABELS[this._lang];
      this._button.title = TITLES[this._lang];
      this._applyLanguage(map);
    });

    // Snapshot originals once style is fully loaded
    const trySnapshot = () => {
      if (map.isStyleLoaded() && !this._originalFields) {
        this._snapshotOriginals(map);
      }
    };
    trySnapshot();
    map.on('idle', trySnapshot);

    // On full style reload (satellite toggle), re-snapshot and re-apply
    let styleVersion = 0;
    map.on('style.load', () => {
      styleVersion++;
      const thisVersion = styleVersion;
      // New style = new originals
      this._originalFields = null;
      if (this._lang !== 'local') {
        // Wait for style to settle, then snapshot new originals and apply language
        setTimeout(() => {
          if (thisVersion === styleVersion) {
            this._snapshotOriginals(map);
            this._applyLanguage(map);
          }
        }, 300);
      }
    });

    this._container.appendChild(this._button);
    return this._container;
  }

  onRemove() {
    this._container.parentNode?.removeChild(this._container);
    this._map = undefined;
  }
}

export default function MapLanguageControl({ position = 'top-right' }) {
  useControl(() => new LanguageControl(), { position });
  return null;
}
